import {
  ActionIcon,
  Button,
  Card,
  Flex,
  Input,
  Select,
  Space,
  Text,
  Title,
} from "@mantine/core";
import styled from "styled-components";
import { doc, getDoc } from "firebase/firestore";
import {
  BILL_SUMMARY,
  SHIPPING_OVERNIGHT_UPS,
  SHIPPING_UNDER_100_NO_RULE,
  SHIPPING_100_OR_GREATER_NO_RULE,
  SHIPPING_UPS_2DAY_AIR,
  SHIPPING_UPS_GROUND,
  SHIPPING_UPS_GROUND_100_OR_GREATER,
} from "../../../constants";
import { IconAlertTriangle, IconCheck, IconChevronDown } from "@tabler/icons-react";
import ConfirmModalFrame from "../../../components/ConfirmModalFrame";
import { modals } from "@mantine/modals";
import { useAppData } from "../../../contexts/AppContext";
import { Step } from "../../../constants/types";
import { Fragment, useEffect, useState } from "react";
import { useForm } from "@mantine/form";
import { BatchRuleActionType } from "../../../constants/enums";
import { db } from "../../../firebase";

const UPS_2_DAY_SHIPPING_VALUE = "18";
const UPS_2_DAY_SHIPPING_SETTING_DOC = "ups2DayShipping";

// Helper to adjust Overnight price based on shipping state (Northeast vs rest)
const NORTHEAST_STATES = new Set([
    "CT",
    "DE",
    "ME",
    "MD",
    "MA",
    "NH",
    "NJ",
    "NY",
    "PA",
    "RI",
    "VT",
    "DC",
  ]);
const NAME_TO_CODE: Record<string, string> = {
    CONNECTICUT: "CT",
    DELAWARE: "DE",
    MAINE: "ME",
    MARYLAND: "MD",
    MASSACHUSETTS: "MA",
    "NEW HAMPSHIRE": "NH",
    "NEW JERSEY": "NJ",
    "NEW YORK": "NY",
    PENNSYLVANIA: "PA",
    "RHODE ISLAND": "RI",
    VERMONT: "VT",
    // DC variations
    "WASHINGTON, D.C.": "DC",
    "WASHINGTON DC": "DC",
    "DISTRICT OF COLUMBIA": "DC",
};

// List of US states for dropdown
const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  {value:  "DC", label: "Washington,D.C"},
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }  
];

const getOvernightPriceForState = (stateRaw: string = "") => {
  const norm = (stateRaw || "").toString().trim().toUpperCase();
  const code = norm.length === 2 ? norm : NAME_TO_CODE[norm] || NAME_TO_CODE[norm.replace(/\./g, "").replace(/\s+/g, " ")] || norm;
  return NORTHEAST_STATES.has(code) ? 33 : 55;
};

const isOvernightOption = (opt: any) =>
  typeof opt?.label === "string" &&
  opt.label.toLowerCase().startsWith("overnight");

const adjustOvernightByState = (options: any[] = [], stateRaw: string = "") => {
  const overnightPrice = getOvernightPriceForState(stateRaw);

  // Map options: if Overnight with value '33' exists, update to the correct price
  return (options || []).map((opt: any) => {
    if (isOvernightOption(opt)) {
      return {
        ...opt,
        label: `Overnight $${overnightPrice}`,
        value: String(overnightPrice),
      };
    }
    return opt;
  });
};

const filterUps2DayShippingOption = (
  options: any[] = [],
  enabled: boolean,
  forceEnabled: boolean = false
) => {
  if (forceEnabled) {
    return options;
  }
  if (enabled) {
    return options;
  }
  return options.filter((option: any) => option?.value !== UPS_2_DAY_SHIPPING_VALUE);
};

const getPreShippingOrderTotal = (meds: any[] = []) => {
  return (
    meds
      ?.filter((med: any) => !med.metadata![BatchRuleActionType.NoPayment])
      ?.reduce(
        (acc: number, med: any) => acc + parseFloat(med.price.replace(/^\D+/g, "")),
        0
      ) || 0
  );
};

export const whichShippingOptions = (order: any, data: any = undefined) => {
  let metadata = order?.metadata || {};

  let meds = data ? data?.medications : order?.medications || order?.meds || [];
  let isAnyOvernight = meds.some((med: any) => med?.overnight);
  let price = getPreShippingOrderTotal(meds);

  const state = data?.addresses?.shippingAddress?.state;
  const hasUnder100Rule = Boolean(metadata![BatchRuleActionType.Under100Shipping]);
  const hasGreaterOrEqual100Rule = Boolean(
    metadata![BatchRuleActionType.GreaterOrEqual100Shipping]
  );
  const isPriceGreaterOrEqual100 = price >= 100;

  const shouldUseGreaterOrEqual100Bucket =
    hasGreaterOrEqual100Rule && !hasUnder100Rule
      ? true
      : hasUnder100Rule && !hasGreaterOrEqual100Rule
      ? false
      : isPriceGreaterOrEqual100;

  if (metadata![BatchRuleActionType.Overnight] || isAnyOvernight) {
    return adjustOvernightByState(SHIPPING_OVERNIGHT_UPS, state);
  } else if (metadata![BatchRuleActionType.UPS2ndDayAirShipping]) {
    return adjustOvernightByState(SHIPPING_UPS_2DAY_AIR, state);
  } else if (metadata![BatchRuleActionType.MustUseUPS]) {
    if (shouldUseGreaterOrEqual100Bucket) {
      return adjustOvernightByState(SHIPPING_UPS_GROUND_100_OR_GREATER, state);
    }
    return adjustOvernightByState(SHIPPING_UPS_GROUND, state);
  } else if (metadata![BatchRuleActionType.OvernightUPS33Shipping]) {
    return adjustOvernightByState(SHIPPING_OVERNIGHT_UPS, state);
  } else if (shouldUseGreaterOrEqual100Bucket) {
    return adjustOvernightByState(SHIPPING_100_OR_GREATER_NO_RULE, state);
  }
  return adjustOvernightByState(SHIPPING_UNDER_100_NO_RULE, state);
};

function AddressDetails() {
  const { appFlow, setData, data, setDisableContinue, order } = useAppData();
  const [isUps2DayShippingEnabled, setIsUps2DayShippingEnabled] = useState(true);

  const form = useForm({
    initialValues: {
      ...{
        addressLine1: "",
        addressLine2: "",
        city: "",
        state: "",
        zipCode: "",
      },
      ...data.addresses.shippingAddress,
    },
    validate: {
      addressLine1: (value) =>
        value === "" ? "Address Line 1 required" : null,
      city: (value) => (value === "" ? "City required" : null),
      state: (value) => (value === "" ? "State required" : null),
      zipCode: (value) => (value === "" ? "Zip Code required" : null),
    },
  });

  // Helper to get shipping options with current form state
  const getCurrentShippingOptions = () => {
    // Create a data object with the current form state instead of stale data
    const currentData = {
      ...data,
      addresses: {
        ...data.addresses,
        shippingAddress: {
          ...form.values,
        },
      },
    };
    const options = whichShippingOptions(order, currentData) || [];
    return filterUps2DayShippingOption(
      options,
      isUps2DayShippingEnabled,
      Boolean(order?.metadata?.[BatchRuleActionType.UPS2ndDayAirShipping])
    );
  };

  const showOvernightDisclaimer = () => {
    modals.open({
      children: (
        <ConfirmModalFrame
          title={`Please note`}
          confirmText="I understand"
          onConfirm={() => {
            modals.close("overnight-shipping-disclaimer");
          }}>
          <div style={{ marginBottom: 20, textAlign: "center" }}>
            <DisclaimerText>
              Shipping speed refers to transit time only. This medication
              requires compounding and may take up to 3 business days to prepare
              before shipment.
            </DisclaimerText>
          </div>
        </ConfirmModalFrame>
      ),
      onClose: () => {},
      modalId: "overnight-shipping-disclaimer",
      closeButtonProps: {
        display: "none",
      },
      centered: true,
      title: "Overnight shipping disclaimer",
      styles: {
        header: {
          display: "none",
        },
      },
    });
  };

  const waiveSignature = () => {
    modals.open({
      children: (
        <ConfirmModalFrame
          title={`Are you sure you want to waive your signature?`}
          message={`Our controlled substance policy requires a signature for delivery. If you waive, you agree:
 
		 For any lost packages, we will need approval from your provider for an early refill and need to charge the full amount for replacement. An investigation with the delivery service will also be mandated following a lost package claim.`}
          onConfirm={() => {
            modals.close("waive-signature");
            setData({
              ...data,
              signatureWaived: true,
            });
          }}
          onCancel={() => {
            modals.close("waive-signature");
          }}
        />
      ),
      onClose: () => {},
      modalId: "waive-signature",
      closeButtonProps: {
        display: "none",
      },
      centered: true,
      title: "Confirm remove",
      styles: {
        header: {
          display: "none",
        },
      },
    });
  };

  useEffect(() => {
    const fetchUps2DayShippingSetting = async () => {
      const settingDoc = await getDoc(doc(db, "settings", UPS_2_DAY_SHIPPING_SETTING_DOC));
      if (settingDoc.exists()) {
        setIsUps2DayShippingEnabled(settingDoc.data().enabled);
        return;
      }
      setIsUps2DayShippingEnabled(true);
    };
    fetchUps2DayShippingSetting();
  }, []);

  useEffect(() => {
    setData((data) => ({
      ...data,
      addresses: {
        ...data.addresses,
        shippingAddress: {
          ...form.values,
        },
      },
    }));
  }, [form.values]);

  useEffect(() => {
    const isOrderForcedToUps2Day = Boolean(
      order?.metadata?.[BatchRuleActionType.UPS2ndDayAirShipping]
    );
    if (
      isUps2DayShippingEnabled ||
      isOrderForcedToUps2Day ||
      data?.shippingOption !== UPS_2_DAY_SHIPPING_VALUE
    ) {
      return;
    }
    setData((prevData) => ({
      ...prevData,
      shippingOption: "",
      totalAmount: prevData.originalAmount ?? 0,
    }));
  }, [data?.shippingOption, isUps2DayShippingEnabled, order?.metadata, setData]);

  // Clear shipping option when state changes to force user to reselect
  useEffect(() => {
    if (data?.shippingOption) {
      // Clear the shipping option so user must make a new selection
      setData({
        ...data,
        shippingOption: "",
        totalAmount: data.originalAmount ?? 0,
      });
    }
  }, [form.values.state]);

  useEffect(() => {
    let shouldBeDisabled = !form.isValid() || !data.shippingOption;
    setDisableContinue({
      [Step.AddressDetails]: shouldBeDisabled,
    });
  }, [data, form.values]);

  useEffect(() => {
    form.setValues({ ...data.addresses.shippingAddress });
  }, [appFlow.currentStep]);

  return (
    <Flex direction={"column"} className="my-prescription-holder">
      <StyledTitle mb={10}>My Prescriptions</StyledTitle>
      <InnerContainer>
        <Flex
          direction={"column"}
          gap={15}
          flex={2}
          className="inner-container-address left-column">
          <Card w={"100%"}>
            <Text style={{ fontSize: 18 }} mb={10}>
              Summary
            </Text>
            <Flex direction={"column"} gap={10}>
              {data.rushOrder ? (
                <Flex justify={"space-between"}>
                  <Text>Rush order</Text>
                  <Text style={{ textAlign: "left" }}>$40</Text>
                </Flex>
              ) : null}
              <Flex justify={"space-between"}>
                <Text>Medicines</Text>
                <Text style={{ textAlign: "left" }}>
                  {new Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(
                    data?.medications
                      ?.filter(
                        (med) => !med.metadata![BatchRuleActionType.NoPayment]
                      )
                      ?.reduce(
                        (acc, med: any) =>
                          acc + parseFloat(med.price.replace(/^\D+/g, "")),
                        0
                      ) || 0
                  )}
                </Text>
              </Flex>

              {BILL_SUMMARY.map((summary) => (
                <Flex justify={"space-between"} key={summary.name}>
                  <Text>{summary.name}</Text>
                  <Text style={{ textAlign: "left" }}>
                    {summary.price ? `$${summary.price}` : "TBD"}
                  </Text>
                </Flex>
              ))}

              {data.shippingOption ? (
                <Flex justify={"space-between"}>
                  <Text>Delivery</Text>
                  <Text style={{ textAlign: "left" }}>
                    $
                    {data.shippingOption &&
                    !isNaN(parseFloat(data.shippingOption))
                      ? parseFloat(data.shippingOption)
                      : 0}
                  </Text>
                </Flex>
              ) : null}

              <Flex gap={8} style={{ alignSelf: "flex-end" }}>
                <Text fw={500}>Total</Text>
                <Text fw={500}>${data?.totalAmount?.toFixed(2)}</Text>
              </Flex>
            </Flex>
          </Card>

          <Fragment>
            <Flex direction={"column"} gap={10}>
              <Subtitle1>
                Shipping Options <span style={{ color: "red" }}>*</span>
              </Subtitle1>

              <DisclaimerBox>
                <Flex align="center" gap={8}>
                  <IconAlertTriangle size={16} color="#054372" />
                  <DisclaimerTitle>Shipping Disclaimer</DisclaimerTitle>
                </Flex>
                <DisclaimerText>
                  During warmer months, orders containing medications with a high
                  risk of melting or damage may not be shipped on Fridays to
                  ensure product integrity.
                </DisclaimerText>
                <DisclaimerText>
                  Air and overnight shipping options do not deliver on
                  Saturdays or Sundays. Orders shipped using these methods will
                  be delivered on the next available business day.
                </DisclaimerText>
              </DisclaimerBox>

              <Select
                // style={{ display: "none" }}
                styles={{
                  input: {
                    padding: 30,
                    border: "none",
                    borderRadius: 8,
                  },
                  section: {
                    paddingRight: 30,
                  },
                }}
                clearable={false}
                allowDeselect={false}
                value={data.shippingOption || null}
                onChange={(value) => {
                  let shippingAmount = 0;
                  if (value && !isNaN(parseFloat(value))) {
                    shippingAmount = parseFloat(value);
                  }
                  setData({
                    ...data,
                    shippingOption: value || "",
                    totalAmount:
                      shippingAmount + (data.originalAmount ?? 0)
                  });

                  const selectedOption = getCurrentShippingOptions().find(
                    (opt: any) => opt?.value === value
                  );
                  if (isOvernightOption(selectedOption)) {
                    showOvernightDisclaimer();
                  }
                }}
                placeholder="Shipping options"
                data={getCurrentShippingOptions()}
                rightSection={
                  <ActionIcon variant="transparent">
                    <IconChevronDown />
                  </ActionIcon>
                }
              />
            </Flex>

            <Flex direction={"column"} gap={10}>
              {order?.medications?.some(
                (med) => med?.metadata![BatchRuleActionType.RequireSignature]
              ) ? (
                <Fragment>
                  {data.signatureWaived ? (
                    <Flex justify={"center"}>
                      <Text>Signature waived </Text>
                      <IconCheck />
                    </Flex>
                  ) : (
                    <>
                      <Subtitle1>
                        Signature required for this delivery.
                      </Subtitle1>
                      <Button
                        className="responsive-button"
                        onClick={waiveSignature}
                        size="lg"
                        radius={"md"}>
                        Would you like to waive your signature
                      </Button>
                    </>
                  )}
                </Fragment>
              ) : null}
            </Flex>
          </Fragment>
        </Flex>
        <Flex direction={"column"} flex={3} gap={15} className="right-column">
          <Card w={"100%"} mih={"100%"}>
            <Text fw={500}>Shipping Address</Text>
            <Text size="sm">Where your medication will be shipped.</Text>

            <Space h="sm" />
            <Flex gap={5}>
              <Text size="sm">Street Address</Text>
              <div className="is-danger">*</div>
            </Flex>
            <Input
              w={"100%"}
              radius="md"
              size="md"
              styles={{
                input: {
                  borderColor: "#6E23CF",
                  borderWidth: "2px",
                },
              }}
              placeholder="Street Address"
              {...form.getInputProps("addressLine1")}
            />
            <Space h="xs" />

            <Text size="sm">Street Address line 2</Text>

            <Input
              w={"100%"}
              radius="md"
              size="md"
              styles={{
                input: {
                  borderColor: "#6E23CF",
                  borderWidth: "2px",
                },
              }}
              placeholder="Street address line 2"
              {...form.getInputProps("addressLine2")}
            />
            <Space h="xs" />

            <Flex
              direction={{ base: "column", sm: "row" }}
              gap={{ base: "sm", sm: "xs" }}>
              <Flex direction={"column"} flex={1}>
                <Flex gap={5}>
                  <Text size="sm">City</Text>
                  <div className="is-danger">*</div>
                </Flex>
                <Input
                  w={"100%"}
                  radius="md"
                  size="md"
                  styles={{
                    input: {
                      borderColor: "#6E23CF",
                      borderWidth: "2px",
                    },
                  }}
                  placeholder="City"
                  {...form.getInputProps("city")}
                />
              </Flex>

              <Flex direction={"column"} flex={1}>
                <Flex gap={5}>
                  <Text size="sm">State / Province</Text>
                  <div className="is-danger">*</div>
                </Flex>
                <Select
                  w={"100%"}
                  radius="md"
                  size="md"
                  styles={{
                    input: {
                      borderColor: "#6E23CF",
                      borderWidth: "2px",
                    },
                  }}
                  placeholder="Select State"
                  data={US_STATES}                  
                  clearable={false}                  
                  {...form.getInputProps("state")}
                />
              </Flex>
            </Flex>
            <Space h="xs" />

            <Flex gap={5}>
              <Text size="sm">Postal / Zip Code</Text>
              <div className="is-danger">*</div>
            </Flex>
            <Input
              maw={"250px"}
              radius="md"
              size="md"
              styles={{
                input: {
                  borderColor: "#6E23CF",
                  borderWidth: "2px",
                },
              }}
              //type="number"
              placeholder="Postal / Zip Code"
              value={form.values.zipCode || ""}
              onChange={(event) => {
                const sanitizedValue = event.currentTarget.value.replace(/\D/g, '').slice(0, 7);
                form.setFieldValue("zipCode", sanitizedValue || "");
              }}
              onBlur={() => form.validateField("zipCode")}
            />
            <Space h="md" />
          </Card>
        </Flex>
      </InnerContainer>
    </Flex>
  );
}

export default AddressDetails;

const InnerContainer = styled.div`
  gap: 15px;
  display: flex;
  width: 100%;
  max-width: 980px;
  margin: 0 auto;

  .left-column {
    flex: 0 0 360px;
    max-width: 360px;
  }

  .right-column {
    flex: 1;
    max-width: 600px;
  }

  @media screen and (max-width: 1500px) {
    flex-direction: column-reverse;

    .left-column,
    .right-column {
      max-width: 100%;
      flex: 1 1 auto;
    }
  }

  @media screen and (max-width: 768px) {
    width: 100%;
  }

  @media screen and (max-width: 480px) {
    height: 100%;    
  }
  @supports (-webkit-touch-callout: none) {
    height: -webkit-fill-available;
  }
  .inner-container-address{
    @media screen and (max-width: 480px) {
      flex-direction: column-reverse !important;
    }
  }
  .my-prescription-holder{
    width: 100%;
  }
`;

const StyledTitle = styled(Title)`
  font-family: DM Sans;
  font-size: 18px;
  font-weight: 600;
  line-height: 25px;
  letter-spacing: 0em;
  text-align: left;
`;

const Subtitle1 = styled.div`
  font-family: Inter;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: -0.011em;
`;

const DisclaimerBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: calc(100% - 16px);
  max-width: 100%;
  box-sizing: border-box;
  margin: 0 auto;
  align-items: flex-start;
  text-align: left;
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid #c7dbef;
  border-left: 4px solid #054372;
  background: #eef4fb;
`;

const DisclaimerTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #001e43;
`;

const DisclaimerText = styled(Text)`
  color: #001e43;
  font-size: 13px;
  line-height: 1.45;
`;
